import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './Reports.css';
import './PublisherOffers.css';

interface PublisherOffersProps {
  publisherUsername: string;
}

const PublisherOffers: React.FC<PublisherOffersProps> = ({ publisherUsername }) => {
  const [publisherData, setPublisherData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableOffers, setAvailableOffers] = useState<any[]>([]);
  const [offerFilter, setOfferFilter] = useState<'all' | 'active' | 'paused'>('active');
  const [expandedOfferId, setExpandedOfferId] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const isAdminView = localStorage.getItem('userType') === 'admin';
        const publisherId = isAdminView ? publisherUsername : localStorage.getItem('publisherId');
        if (!publisherId) {
          throw new Error('Publisher ID not found. Please login again.');
        }

        const publisherResponse = isAdminView
          ? await apiService.getPublisher(parseInt(publisherId, 10))
          : await apiService.getPublisherMe();
        const publisher = publisherResponse.publisher;

        if (publisher) {
          setPublisherData({
            id: publisher.id,
            name: publisher.name || '',
          });

          // Fetch offers assigned to this publisher
          const offersResponse = await apiService.getPublisherOffers(publisher.id);
          const assignedOffers = (offersResponse.offers || []).map((offer: any) => ({
            id: offer.id,
            name: offer.name || '',
            payout: parseFloat(offer.payout) || 0,
            payoutType: offer.goal_type || 'Unknown',
            countries: offer.countries || ['All'],
            os: offer.os || ['All'],
            status: offer.is_active ? 'active' : 'inactive',
            dailyCap: offer.daily_cap || 0,
            currentVolume: offer.total_clicks || 0,
            previewUrl: offer.preview_url || '',
            description: offer.description || '',
            restrictions: offer.restrictions || {}
          }));
          setAvailableOffers(assignedOffers);
        } else {
          setError('Publisher not found');
        }

      } catch (err: any) {
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [publisherUsername]);

  const generateTrackingLink = (offerId: number) => {
    // Use API base URL from environment, fallback to localhost for development
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';
    const baseUrl = `${apiBaseUrl}/tracking`;
    const publisherId = publisherData.id;
    return `${baseUrl}?aff_id=${publisherId}&offer_id=${offerId}&aff_sub1={click_id}&aff_sub2={affiliate_id}&gaid={google_id}&ifaid={ifa_id}&device_id={device_id}&aff_sub3={app_name}`;
  };

  // Filter offers based on selected filter
  const filteredOffers = availableOffers.filter(offer => {
    if (offerFilter === 'all') return true;
    if (offerFilter === 'active') return offer.status === 'active';
    if (offerFilter === 'paused') return offer.status === 'inactive';
    return true;
  });

  const toggleOffer = (offerId: number) => {
    setExpandedOfferId((current) => (current === offerId ? null : offerId));
  };

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>, offerId: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleOffer(offerId);
    }
  };

  const copyTrackingLink = async (offerId: number) => {
    const link = generateTrackingLink(offerId);
    await navigator.clipboard.writeText(link);
    alert('Tracking link copied to clipboard!');
  };

  if (loading) {
    return <div className="loading">Loading offers...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="publisher-offers-page">
      <div className="publisher-offers-header">
        <div>
          <h1>Available Offers</h1>
          <p>Browse and promote offers assigned to you</p>
        </div>
        <div className="publisher-offers-filters">
          <button
            className={`btn ${offerFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setOfferFilter('all')}
          >
            All ({availableOffers.length})
          </button>
          <button
            className={`btn ${offerFilter === 'active' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setOfferFilter('active')}
          >
            Active ({availableOffers.filter(o => o.status === 'active').length})
          </button>
          <button
            className={`btn ${offerFilter === 'paused' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setOfferFilter('paused')}
          >
            Paused ({availableOffers.filter(o => o.status === 'inactive').length})
          </button>
        </div>
      </div>

      <div className="report-content publisher-offers-content">
        <div className="table-container publisher-offers-table-container">
          <table className="report-table publisher-offers-table">
            <thead>
              <tr>
                <th>Offer ID</th>
                <th>Offer Name</th>
                <th>Tracking Link</th>
                <th>Payout Type</th>
                <th>Payout</th>
                <th>Daily Cap</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredOffers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-cell">
                    No offers found for the selected filter.
                  </td>
                </tr>
              ) : (
                filteredOffers.map((offer) => {
                  const isExpanded = expandedOfferId === offer.id;
                  return (
                    <React.Fragment key={offer.id}>
                      <tr
                        className={`offer-row ${isExpanded ? 'is-expanded' : ''}`}
                        onClick={() => toggleOffer(offer.id)}
                        onKeyDown={(event) => handleRowKeyDown(event, offer.id)}
                        tabIndex={0}
                        role="button"
                        aria-expanded={isExpanded}
                      >
                        <td>{offer.id}</td>
                        <td>
                          <div className="offer-cell">
                            <span className="offer-name">{offer.name}</span>
                          </div>
                        </td>
                        <td>
                          <div className="tracking-link-cell">
                            <button
                              className="btn btn-info btn-sm"
                              onClick={(event) => {
                                event.stopPropagation();
                                void copyTrackingLink(offer.id);
                              }}
                            >
                              Copy
                            </button>
                          </div>
                        </td>
                        <td>{offer.payoutType}</td>
                        <td>
                          <span className="payout">${offer.payout.toFixed(2)}</span>
                        </td>
                        <td>{offer.dailyCap.toLocaleString()}</td>
                        <td>
                          <span className={`status-badge ${offer.status === 'active' ? 'success' : 'warning'}`}>
                            {offer.status === 'active' ? 'Active' : 'Paused'}
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="offer-details-row">
                          <td colSpan={7}>
                            <div className="offer-details-panel">
                              <div className="offer-tracking-row">
                                <span className="offer-detail-label">Tracking Link:</span>
                                <span className="offer-tracking-link">{generateTrackingLink(offer.id)}</span>
                              </div>
                              <div className="offer-details-grid offer-details-grid-simple">
                                <div className="offer-detail-item">
                                  <span className="offer-detail-label">Preview</span>
                                  {offer.previewUrl ? (
                                    <a href={offer.previewUrl} target="_blank" rel="noopener noreferrer">
                                      Open preview
                                    </a>
                                  ) : (
                                    <span>N/A</span>
                                  )}
                                </div>
                                <div className="offer-detail-item">
                                  <span className="offer-detail-label">Countries</span>
                                  <span>{offer.countries.join(', ')}</span>
                                </div>
                                <div className="offer-detail-item">
                                  <span className="offer-detail-label">OS</span>
                                  <span>{offer.os.join(', ')}</span>
                                </div>
                                <div className="offer-detail-item">
                                  <span className="offer-detail-label">Description</span>
                                  <span>{offer.description || 'N/A'}</span>
                                </div>
                                <div className="offer-detail-item">
                                  <span className="offer-detail-label">Restrictions</span>
                                  <span>
                                    {Object.keys(offer.restrictions).length > 0
                                      ? JSON.stringify(offer.restrictions)
                                      : 'None'}
                                  </span>
                                </div>
                                <div className="offer-detail-item">
                                  <span className="offer-detail-label">Current Volume</span>
                                  <span>{offer.currentVolume.toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PublisherOffers;
